import { computed } from '@angular/core'

import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals'

import { HarnessAnswer } from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import { HarnessSteering } from '@flowx/designer/modules/ai-builder/types/harness.types'
import { BriefDecision } from '@flowx/designer/modules/ai-builder/utils/brief-display.util'

/** Brief steering staged before it goes out. All three kinds travel in one send — one revision
 * rather than two racing — and always as a `respond`, never an approval. */
export type HarnessPendingSteering = {
  changes: Readonly<Record<string, string>>
  outOfScope: readonly string[]
  /** Keyed by the question's own text: the only handle a question has, and what the harness
   * matches on. */
  answers: Readonly<Record<string, string>>
}

type HarnessReviewState = {
  steering: HarnessPendingSteering
  /** The maker pressed approve with blockers outstanding, so the confirm step replaces the
   * buttons rather than opening a dialog over them. */
  confirmingApproval: boolean
}

const initialReviewState: HarnessReviewState = {
  steering: { changes: {}, outOfScope: [], answers: {} },
  confirmingApproval: false,
}

/**
 * What the maker has done to the document in front of them but not yet sent: a decision flipped,
 * something ruled out, a question answered — and the one confirmation that stands between a
 * blocked proposal and its approval.
 *
 * A form model, kept apart from the session that carries it. The session store reads this on its
 * way out and clears it, so the dependency runs one way: nothing here knows about the stream, the
 * interrupt or the transport, and staging an edit cannot fail.
 *
 * Every mutator drops `confirmingApproval`, because a maker who edits after pressing approve has
 * changed their mind about approving, and a confirmation left standing over a changed document is
 * a confirmation of something they were never shown.
 */
export const HarnessReviewStore = signalStore(
  { providedIn: 'root' },
  withState<HarnessReviewState>(initialReviewState),
  withComputed(({ steering }) => ({
    hasSteering: computed(
      () =>
        !!Object.keys(steering().changes).length ||
        !!steering().outOfScope.length ||
        !!Object.keys(steering().answers).length
    ),
  })),
  withMethods((store) => ({
    /** Flip a decision to one of its rejected alternatives, or clear the flip. */
    chooseAlternative: (decisionId: string, choice: string): void =>
      patchState(store, ({ steering }) => {
        const { [decisionId]: current, ...rest } = steering.changes

        return {
          confirmingApproval: false,
          steering: {
            ...steering,
            changes: current === choice ? rest : { ...rest, [decisionId]: choice },
          },
        }
      }),

    addOutOfScope: (text: string): void => {
      const trimmed = text.trim()

      if (trimmed) {
        patchState(store, ({ steering }) => ({
          confirmingApproval: false,
          steering: { ...steering, outOfScope: [...steering.outOfScope, trimmed] },
        }))
      }
    },

    removeOutOfScope: (index: number): void =>
      patchState(store, ({ steering }) => ({
        confirmingApproval: false,
        steering: {
          ...steering,
          outOfScope: steering.outOfScope.filter((_, position) => position !== index),
        },
      })),

    /**
     * Answer one of the brief's open questions, or clear the answer.
     *
     * Staged rather than sent: a maker answering two questions should get one revision that
     * accounts for both, not two revisions racing each other.
     */
    answerQuestion: (question: string, answer: string): void =>
      patchState(store, ({ steering }) => {
        const { [question]: current, ...rest } = steering.answers
        const trimmed = answer.trim()

        return {
          confirmingApproval: false,
          steering: {
            ...steering,
            answers: trimmed ? { ...rest, [question]: trimmed } : rest,
          },
        }
      }),

    /** Ask for the confirmation that stands in for a disabled approve. */
    confirmApproval: (): void => patchState(store, { confirmingApproval: true }),

    cancelApproval: (): void => patchState(store, { confirmingApproval: false }),

    /** Nothing is staged any more: the turn it rode went out, or the conversation moved to
     * another version. */
    clear: (): void => patchState(store, initialReviewState),

    /** What goes on the wire. */
    payload: (): HarnessSteering => toSteeringPayload(store.steering()),

    /** What the transcript records. */
    describe: (decisions: readonly BriefDecision[]): HarnessAnswer[] =>
      describeSteering(store.steering(), decisions),
  }))
)

/** The maker's steering as settled lines for the transcript — not what goes on the wire, so it
 * only has to read as what the maker did. A decision is named by its heading, and by its id only
 * when the brief under review no longer carries it. */
function describeSteering(
  steering: HarnessPendingSteering,
  decisions: readonly BriefDecision[]
): HarnessAnswer[] {
  const byId = new Map(decisions.map((decision) => [decision.decisionId, decision]))

  return [
    ...Object.entries(steering.changes).map(([decisionId, choice]): HarnessAnswer => {
      const decision = byId.get(decisionId)
      const from = decision?.chosen ? ` instead of “${decision.chosen}”` : ''

      return {
        subject: decision?.decision || decisionId,
        text: `“${choice}”${from}`,
        // The fork, when the brief offered one, so the row's hover says what was being decided
        // rather than repeating its heading.
        ...(decision?.question && { question: decision.question }),
      }
    }),
    ...steering.outOfScope.map((text): HarnessAnswer => ({ subject: 'Ruled out', text })),
    ...Object.entries(steering.answers).map(([question, answer]): HarnessAnswer => ({
      subject: question,
      text: answer,
    })),
  ]
}

function toSteeringPayload(steering: HarnessPendingSteering): HarnessSteering {
  return {
    change_decisions: Object.entries(steering.changes).map(([decision_id, choose]) => ({
      decision_id,
      choose,
    })),
    add_out_of_scope: [...steering.outOfScope],
    answer_questions: Object.entries(steering.answers).map(([question, answer]) => ({
      question,
      answer,
    })),
  }
}
